/**
 * Envelope `type` tokens that mean something on BOTH the capture and the
 * playback side, on every platform.
 *
 * The server validates `type` for charset and length only (1-60 of
 * `[A-Za-z0-9._-]`) and never for membership, so these are a client-side
 * vocabulary, not a server contract. They live here because the web recorder,
 * the NativeScript recorder and the player must agree on them exactly: the
 * player demultiplexes the stream by these strings, and a mobile recorder that
 * emitted `console` instead of `Console` would store fine, answer 202, and show
 * up nowhere in the player's devtools rail.
 *
 * Platform-specific tokens do NOT belong here. rrweb's numeric-type table stays
 * with the rrweb backend. The `MobileFrame` / `MobileTouch` tokens are shared,
 * but in `ryn-replay-mobile-frame.ts` alongside the tree schema they belong to:
 * they are a contract between one recorder and the player, not vocabulary every
 * recorder emits.
 */

/** App-level events recorded via `track()`. */
export const RYN_REPLAY_APP_EVENT_TYPE = 'AppEvent';

/** The signed-in user, with the full identity held at that moment. */
export const RYN_REPLAY_IDENTITY_EVENT_TYPE = 'Identity';

/** One-shot device / build / environment snapshot at session start. */
export const RYN_REPLAY_CONTEXT_EVENT_TYPE = 'Context';

/** Screen or window geometry, emitted on change. */
export const RYN_REPLAY_VIEWPORT_EVENT_TYPE = 'Viewport';

/** Captured `console.*` output. */
export const RYN_REPLAY_CONSOLE_EVENT_TYPE = 'Console';

/** Captured HTTP request/response metadata. */
export const RYN_REPLAY_NETWORK_EVENT_TYPE = 'Network';

/** Closes a client session segment (#31331). */
export const RYN_REPLAY_SESSION_END_EVENT_TYPE = 'SessionEnd';

/** Opens a continued capture segment (#31331). */
export const RYN_REPLAY_SESSION_START_EVENT_TYPE = 'SessionStart';

/**
 * The session keyframe.
 *
 * Named here rather than in the rrweb backend because three separate pieces of
 * platform-neutral machinery branch on it and each one held its own copy of the
 * literal: the retry queue refuses to evict a batch containing one, batch-fit
 * refuses to replace one with an omission marker, and the offline store reports
 * whether a persisted batch holds one. All three protect the same invariant —
 * lose the keyframe and every later delta in the session is unplayable — so
 * they have to agree on the spelling, and three copies of a string is how they
 * stop agreeing.
 *
 * A NativeScript full view-tree frame carries the same meaning for the mobile
 * renderer, so it reuses this token rather than inventing a second keyframe
 * name that the shared eviction rules would not recognize and would therefore
 * happily discard.
 */
export const RYN_REPLAY_FULL_SNAPSHOT_EVENT_TYPE = 'FullSnapshot';
